import { useMemo } from 'react';

import { TreeEntry, isTreeFolder } from './document-list-to-tree';

/**
 * Utility function to extract the first number from a folder name
 * @param folderName - The name of the folder
 * @returns The first number found, or Number.MAX_SAFE_INTEGER if no number found
 */
const extractFirstNumber = (folderName: string): number => {
  const words = parseInt(folderName.split(' ')[0]);
  return isNaN(words) ? Number.MAX_SAFE_INTEGER : words;
};

/**
 * Custom hook that sorts tree entries numerically by the first number in their names
 * @param content - The tree entry to sort children for
 * @returns Sorted array of [key, entry] tuples from the tree
 */
export const useSortedTreeEntries = (content: TreeEntry) => {
  return useMemo(() => {
    if (!isTreeFolder(content)) return [];

    const items = [...content.children.entries()];
    return items.sort(([, x], [, y]) => {
      const nameX = x.toDisplayString();
      const nameY = y.toDisplayString();
      const numberX = extractFirstNumber(nameX);
      const numberY = extractFirstNumber(nameY);

      // If numbers are different, sort numerically
      if (numberX !== numberY) {
        return numberX - numberY; // Sort from low to high
      }

      // If numbers are the same, sort alphabetically for consistency
      return nameX.localeCompare(nameY);
    });
  }, [content]);
};
